import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const TutorialContext = createContext();

export const useTutorial = () => {
    const context = useContext(TutorialContext);
    if (!context) {
        throw new Error('useTutorial must be used within TutorialProvider');
    }
    return context;
};

export const TutorialProvider = ({ children }) => {
    const [isTutorialMode, setIsTutorialMode] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [currentFlow, setCurrentFlow] = useState('onboarding');
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const [tutorialHistory, setTutorialHistory] = useState([]);
    const [isPaused, setIsPaused] = useState(false);
    const [showTutorialButton, setShowTutorialButton] = useState(true);
    const [modalCleanupCallbacks, setModalCleanupCallbacks] = useState([]);

    const tutorialFlows = React.useMemo(() => ({
        onboarding: {
            name: 'Getting Started',
            steps: [
                { id: 'welcome', type: 'modal', title: 'Welcome to Daily Learning Hub' },
                { id: 'add-topic', type: 'spotlight', target: 'add-topic-button', title: 'Add Your First Topic' },
                { id: 'topic-card', type: 'spotlight', target: 'topic-card-dummy-1', title: 'Topic Cards & Status Indicators' },
                { id: 'topic-grouping', type: 'spotlight', target: 'topic-groups-area', title: 'Organize with Topic Groups' },
                { id: 'add-tip', type: 'spotlight', target: 'add-tip-button', title: 'Creating Learning Concepts' },
                { id: 'tip-card', type: 'spotlight', target: 'tip-card', title: 'Concept Cards' },
                { id: 'new-concept-modal', type: 'modal', target: 'new-concept-modal', title: 'Knowledge Level Selection' },
                { id: 'knowledge-level-tip-panel', type: 'spotlight', target: 'knowledge-level-selector', title: 'Adjust Knowledge Level Anytime' },
                { id: 'tip-content', type: 'spotlight', target: 'tip-content-area', title: 'Reading Your Concept' },
                { id: 'ai-tutor', type: 'spotlight', target: 'ai-tutor-area', title: 'AI Tutor Conversations' },
                { id: 'quick-actions', type: 'spotlight', target: 'quick-actions-area', title: 'Quick Actions' },
                { id: 'tristate-buttons', type: 'spotlight', target: 'tristate-buttons-area', title: 'View Modes' },
                { id: 'tip-actions', type: 'spotlight', target: 'tip-actions-area', title: 'Actions' },
                { id: 'review-tracking', type: 'spotlight', target: 'review-tracking-area', title: 'Review System' },
                { id: 'review-modal', type: 'modal', target: 'review-question-modal', title: 'Test Your Knowledge' },
                { id: 'scheduled-generation', type: 'modal', target: 'edit-topic-modal', title: 'Scheduled Concept Generation' },
                { id: 'filter-functions', type: 'spotlight', target: 'topic-filters,tip-filters', title: 'Smart Filtering' },
                { id: 'ai-learning-summary', type: 'modal', target: 'edit-topic-modal', title: 'AI Learning Summary' },
                { id: 'complete', type: 'modal', title: 'Tutorial Complete!' }
            ]
        },
        advanced: {
            name: 'Advanced Features',
            steps: [
                { id: 'topic-groups', type: 'spotlight', target: 'topic-groups', title: 'Organizing Topics' },
                { id: 'drag-drop', type: 'spotlight', target: 'topic-card', title: 'Drag & Drop Topics' },
                { id: 'settings', type: 'spotlight', target: 'settings-button', title: 'Customize Your Experience' },
                { id: 'analytics', type: 'spotlight', target: 'analytics-button', title: 'Track Your Progress' }
            ]
        },
        aiTutor: {
            name: 'AI Tutor Deep Dive',
            steps: [
                { id: 'conversation-start', type: 'modal', title: 'Starting a Conversation' },
                { id: 'view-modes', type: 'spotlight', target: 'view-mode-toggle', title: 'View Modes' },
                { id: 'conversation-history', type: 'spotlight', target: 'conversation-history', title: 'Conversation History' },
                { id: 'follow-up', type: 'modal', title: 'Asking Follow-up Questions' }
            ]
        }
    }), []);

    const startTutorial = useCallback((flowName = 'onboarding') => {
        setIsTutorialMode(true);
        setCurrentFlow(flowName);
        setCurrentStep(0);
        setIsPaused(false);
        setTutorialHistory(prev => [...prev, { flow: flowName, startedAt: new Date() }]);
    }, []);

    const exitTutorial = useCallback(() => {
        // Execute all modal cleanup callbacks
        modalCleanupCallbacks.forEach(callback => {
            if (typeof callback === 'function') {
                callback();
            }
        });
        // Clear the callbacks after executing
        setModalCleanupCallbacks([]);
        
        setIsTutorialMode(false);
        setCurrentStep(0);
        setIsPaused(false);
    }, [modalCleanupCallbacks]);

    const nextStep = useCallback(() => {
        const flow = tutorialFlows[currentFlow];
        if (currentStep < flow.steps.length - 1) {
            const stepId = `${currentFlow}-${flow.steps[currentStep].id}`;
            setCompletedSteps(prev => new Set([...prev, stepId]));
            setCurrentStep(prev => prev + 1);
        } else {
            const flowCompleteId = `${currentFlow}-complete`;
            setCompletedSteps(prev => new Set([...prev, flowCompleteId]));
            exitTutorial();
        }
    }, [currentFlow, currentStep, exitTutorial, tutorialFlows]);

    const previousStep = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    }, [currentStep]);

    const jumpToStep = useCallback((stepIndex) => {
        if (stepIndex >= 0 && stepIndex < tutorialFlows[currentFlow].steps.length) {
            setCurrentStep(stepIndex);
        }
    }, [currentFlow, tutorialFlows]);

    const pauseTutorial = useCallback(() => {
        setIsPaused(true);
    }, []);

    const resumeTutorial = useCallback(() => {
        setIsPaused(false);
    }, []);

    const skipTutorial = useCallback(() => {
        const flow = tutorialFlows[currentFlow];
        flow.steps.forEach((step, index) => {
            const stepId = `${currentFlow}-${step.id}`;
            setCompletedSteps(prev => new Set([...prev, stepId]));
        });
        exitTutorial();
    }, [currentFlow, exitTutorial, tutorialFlows]);

    const registerModalCleanup = useCallback((callback) => {
        setModalCleanupCallbacks(prev => [...prev, callback]);
    }, []);

    const unregisterModalCleanup = useCallback((callback) => {
        setModalCleanupCallbacks(prev => prev.filter(cb => cb !== callback));
    }, []);

    const resetTutorial = useCallback(() => {
        setCompletedSteps(new Set());
        setTutorialHistory([]);
    }, []);

    const isStepCompleted = useCallback((flowName, stepId) => {
        return completedSteps.has(`${flowName}-${stepId}`);
    }, [completedSteps]);

    const isFlowCompleted = useCallback((flowName) => {
        return completedSteps.has(`${flowName}-complete`);
    }, [completedSteps]);

    const getCurrentStepData = useCallback(() => {
        if (!isTutorialMode || !tutorialFlows[currentFlow]) return null;
        return tutorialFlows[currentFlow].steps[currentStep];
    }, [isTutorialMode, currentFlow, currentStep, tutorialFlows]);

    const getTutorialProgress = useCallback(() => {
        if (!isTutorialMode || !tutorialFlows[currentFlow]) return { current: 0, total: 0, percentage: 0 };
        const total = tutorialFlows[currentFlow].steps.length;
        return {
            current: currentStep + 1,
            total,
            percentage: Math.round(((currentStep + 1) / total) * 100)
        };
    }, [isTutorialMode, currentFlow, currentStep, tutorialFlows]);

    useEffect(() => {
        const savedTutorialState = localStorage.getItem('tutorialState');
        if (savedTutorialState) {
            try {
                const state = JSON.parse(savedTutorialState);
                setCompletedSteps(new Set(state.completedSteps || []));
                setTutorialHistory(state.tutorialHistory || []);
                setShowTutorialButton(true);
            } catch (error) {
                console.error('Failed to load tutorial state:', error);
            }
        }
    }, []);

    useEffect(() => {
        const state = {
            completedSteps: Array.from(completedSteps),
            tutorialHistory,
            showTutorialButton
        };
        localStorage.setItem('tutorialState', JSON.stringify(state));
    }, [completedSteps, tutorialHistory, showTutorialButton]);

    const value = {
        isTutorialMode,
        currentStep,
        currentFlow,
        completedSteps,
        tutorialHistory,
        isPaused,
        showTutorialButton,
        tutorialFlows,
        startTutorial,
        exitTutorial,
        nextStep,
        previousStep,
        jumpToStep,
        pauseTutorial,
        resumeTutorial,
        skipTutorial,
        resetTutorial,
        isStepCompleted,
        isFlowCompleted,
        getCurrentStepData,
        getTutorialProgress,
        setShowTutorialButton,
        registerModalCleanup,
        unregisterModalCleanup
    };

    return (
        <TutorialContext.Provider value={value}>
            {children}
        </TutorialContext.Provider>
    );
};